import { useEffect, useRef, useState } from 'react'
import { getRacingTicket, dropRacingTicket } from '../services/racingTicket'

const FLUCS_URL = 'https://api.rapidoddsapi.com/racing/flucs'
const SLOW_MS = 60 * 1000              // re-fetch every 60s...
const FAST_MS = 15 * 1000              // ...every 15s...
const FAST_INSIDE_MS = 5 * 60 * 1000   // ...inside 5 minutes of the jump

// Price history (flucs) for ONE race, the one being viewed, straight from the
// racing API with the same ticket as the odds WebSocket. One request covers
// every book and runner. Stops once the race has closed in the odds feed.
// The live price still comes from the WebSocket: flucs can lag a snapshot.
//
// Returns null until loaded, then { raceId, byKey, median }:
//   byKey:  'Book|number' -> [{ t, win, place }] oldest first
//   median: { win, place }, each number -> [{ t, price }], the median across
//           books at every snapshot, repeats dropped
export default function useRacingFlucs({ race, enabled }) {
  const [flucs, setFlucs] = useState(null)
  const raceRef = useRef(race)
  raceRef.current = race
  const raceId = race?.race_id

  useEffect(() => {
    if (!enabled || !raceId) return
    let stopped = false
    let timer = null

    const schedule = (delay) => {
      if (stopped || raceRef.current?.closed) return
      const left = (raceRef.current?.start ?? Infinity) - Date.now()
      timer = setTimeout(load, delay ?? (left < FAST_INSIDE_MS ? FAST_MS : SLOW_MS))
    }

    const load = async (retried = false) => {
      // A background tab waits; coming back fetches straight away.
      if (document.hidden) return schedule()
      const { ticket, noAccess } = await getRacingTicket()
      if (stopped) return
      if (!ticket) return noAccess ? undefined : schedule()

      let res
      try {
        res = await fetch(`${FLUCS_URL}?race_id=${encodeURIComponent(raceId)}&ticket=${encodeURIComponent(ticket)}`)
      } catch {
        return schedule()
      }
      if (stopped) return
      if (res.status === 401 && !retried) {
        dropRacingTicket(ticket)
        return load(true)
      }
      if (res.status === 403 || res.status === 400) return
      if (res.status === 429) {
        const secs = Number(res.headers.get('Retry-After'))
        return schedule(secs > 0 ? secs * 1000 : SLOW_MS)
      }
      if (res.ok) {
        try {
          const body = await res.json()
          if (!stopped) setFlucs(indexFlucs(raceId, body))
        } catch {
          // a bad body is skipped; the next fetch tries again
        }
      }
      schedule()
    }

    const onVisibilityChange = () => {
      if (document.hidden) return
      clearTimeout(timer)
      load()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    load()
    return () => {
      stopped = true
      clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [raceId, enabled])

  return flucs
}

function indexFlucs(raceId, body) {
  const byKey = new Map()
  const perRunner = new Map()   // number -> one points list per book
  for (const bk of body.bookmakers || []) {
    for (const rn of bk.runners || []) {
      const points = (rn.flucs || [])
        .map((f) => ({ t: Date.parse(f.time), win: f.win ?? null, place: f.place ?? null }))
        .filter((p) => !Number.isNaN(p.t))
        .sort((a, b) => a.t - b.t)
      if (!points.length) continue
      byKey.set(`${bk.bookmaker}|${rn.number}`, points)
      if (!perRunner.has(rn.number)) perRunner.set(rn.number, [])
      perRunner.get(rn.number).push(points)
    }
  }
  return {
    raceId,
    byKey,
    median: { win: medianLines(perRunner, 'win'), place: medianLines(perRunner, 'place') },
  }
}

// At every snapshot time, each book's price as it stood then (its last point
// at or before it), and the median of those. A median, not an average, so one
// stale or odd book barely moves it.
function medianLines(perRunner, market) {
  const out = new Map()
  for (const [number, lists] of perRunner) {
    const times = [...new Set(lists.flatMap((pts) => pts.map((p) => p.t)))].sort((a, b) => a - b)
    const at = lists.map(() => -1)
    const line = []
    for (const t of times) {
      const prices = []
      lists.forEach((pts, i) => {
        while (at[i] + 1 < pts.length && pts[at[i] + 1].t <= t) at[i]++
        const p = at[i] >= 0 ? pts[at[i]][market] : null
        if (p != null) prices.push(p)
      })
      if (!prices.length) continue
      const price = median(prices)
      if (price !== line[line.length - 1]?.price) line.push({ t, price })
    }
    out.set(number, line)
  }
  return out
}

function median(values) {
  const v = [...values].sort((a, b) => a - b)
  const mid = Math.floor(v.length / 2)
  return v.length % 2 ? v[mid] : (v[mid - 1] + v[mid]) / 2
}
